import React, { useState, useEffect, useContext, useCallback } from 'react';
import './Header.css';
import { NavLink } from "react-router-dom";
import home from './icon/house-icon.png';
import homefill from './icon/house-fill.png';
import shop from './icon/shop.png';
import shopf from './icon/shopf.png';
import bell from './icon/bell.png';
import bellf from './icon/bellf.png';
import up from './icon/up.png';
import upf from './icon/upf.png';
import page from './icon/page.png';
import pagef from './icon/pagef.png';
import cart from './icon/cart.png';
import cartf from './icon/cartf.png';
import box from './icon/box.png';
import boxf from './icon/boxfill.png';
import CartContext from './CartContext';
import OrdersContext from './OrdersContext';
import { FaBars, FaTimes } from 'react-icons/fa';
import logo from './images/logoh.png';

const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

// ── Module-level notification cache ──────────────────────────────────────────
// Prevents Header re-mounting on navigation from re-firing /api/notifications.
let _notifCache   = null;
let _notifLastFetch = 0;
const NOTIF_TTL   = 30 * 1000; // 30 seconds
const NOTIF_POLL  = 60 * 1000; // poll every 60s (was 30s)

function Header() {
  const [isMenuOpen, setIsMenuOpen]   = useState(false);
  const { cartCount }                 = useContext(CartContext);
  const [unreadCount, setUnreadCount] = useState(_notifCache?.unread ?? 0);
  const { activeOrdersCount }         = useContext(OrdersContext);
  const [isMobile, setIsMobile]       = useState(window.innerWidth <= 768);


  const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

  // ── window resize ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) setIsMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // ── notifications only — cart and orders are handled by their own Contexts ─
  // Previously Header called fetchCartCount() + fetchOrdersCount() on an interval
  // AND those Contexts had their own intervals → every endpoint polled twice.
  // Now Header only owns notifications; CartContext and OrdersContext own theirs.
  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Cache: skip if fetched recently
    if (_notifCache && Date.now() - _notifLastFetch < NOTIF_TTL) {
      setUnreadCount(_notifCache.unread);
      return;
    }

    const t0 = performance.now();
    try {
      const response = await fetch(`${API_URL}/api/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const elapsed = performance.now() - t0;
      if (elapsed > 2000) {
        console.error(`[Header][SLOW] /api/notifications took ${(elapsed/1000).toFixed(2)}s (>2s)`);
      }
      if (response.ok) {
        const notifications = await response.json();
        const unread = notifications.filter(n => !n.is_read).length;
        _notifCache   = { unread };
        _notifLastFetch = Date.now();
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, NOTIF_POLL);

    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        _notifCache = null; // bust cache on auth change
        if (localStorage.getItem('token')) fetchNotifications();
        else setUnreadCount(0);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchNotifications]);

  const toggleMenu = () => setIsMenuOpen(prev => !prev);

  const navLinks = [
    { to: "/Home",          icon: home,  activeIcon: homefill, text: "Home" },
    { to: "/Pages",         icon: page,  activeIcon: pagef,    text: "Pages" },
    { to: "/Shop",          icon: shop,  activeIcon: shopf,    text: "Shop" },
    { to: "/notifications", icon: bell,  activeIcon: bellf,    text: "Notifications",
      badge: unreadCount > 0 ? unreadCount : null },
    { to: "/Cart",          icon: cart,  activeIcon: cartf,    text: "Cart",
      badge: cartCount > 0 ? cartCount : null },
    { to: "/Orders",        icon: box,   activeIcon: boxf,     text: "Orders",
      badge: activeOrdersCount > 0 ? activeOrdersCount : null },
    { to: "/User",          icon: up,    activeIcon: upf,      text: "Profile" },
  ];

  return (
    <header className="header-nav">
      <nav className="navbar">
        <div className="logo-search">
          <div className="logo-container">
            <NavLink to="/Home" className="logo">
              <img src={logo} alt="Logo" style={{ width: '85px', height: 'auto' }} />
            </NavLink>
          </div>
          {isMobile && (
            <div className="mobile-menu-btn" onClick={toggleMenu}>
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </div>
          )}
        </div>

        <div className="nav-links">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              {({ isActive }) => (
                <>
                  <span style={{ position: "relative", display: "inline-block" }}>
                    <img src={isActive ? link.activeIcon : link.icon} alt={link.text} />
                    {link.badge !== null && link.badge > 0 && (
                      <span className="nav-badge">{link.badge}</span>
                    )}
                  </span>
                  <span>{link.text}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {isMobile && isMenuOpen && (
        <div className="mobile-dropdown-nav">
          <div className="dropdown-content">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `dropdown-item ${isActive ? "active" : ""}`}
                onClick={() => setIsMenuOpen(false)}
              >
                {({ isActive }) => (
                  <>
                    <img src={isActive ? link.activeIcon : link.icon} alt={link.text} />
                    <span>{link.text}</span>
                    {link.badge !== null && link.badge > 0 && (
                      <span className="mobile-badge">{link.badge}</span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;